/**
 * The API's own reason for refusing a request, as one sentence an operator can act on.
 *
 * Django REST Framework answers a refused save with the field it objected to and why — `{phone_number: ["Enter a
 * valid phone number."]}` — and the pages that save users threw that away and said "Failed to add the user". The
 * global banner (myFunction in global.js) does show the text, but without the field, so "Enter a valid phone
 * number." could be about any of the three numbers on the form. This keeps the field name with its message.
 *
 * Nested objects (contact_address.postal_code) are named by their full path. Returns null when the error carries
 * no reason at all — a dropped connection, a cancelled request — so the caller keeps its own wording.
 *
 * @param {Object} error - the rejection axios handed back
 * @returns {string|null}
 */
export function apiErrorReason(error) {
    const data = error?.response?.data;
    if (data === null || data === undefined || data === "") return null;

    const reasons = [];
    collect(data, [], reasons);

    return reasons.length ? reasons.join(" ") : null;
}

// Keys whose message is not about any one field, so naming the key would only add noise.
const GENERAL_KEYS = new Set(["detail", "error", "message", "non_field_errors"]);

function collect(value, path, reasons) {
    if (value === null || value === undefined) return;

    if (Array.isArray(value)) {
        value.forEach((item) => collect(item, path, reasons));
        return;
    }

    if (typeof value === "object") {
        Object.entries(value).forEach(([key, inner]) => (
            collect(inner, GENERAL_KEYS.has(key) ? path : [...path, key], reasons)
        ));
        return;
    }

    const text = String(value).trim();
    if (!text) return;

    // An HTML error page (nginx's 502, Django's debug page) is not a reason anyone can read in a toast.
    if (text.startsWith("<")) return;

    reasons.push(path.length ? `${path.map(label).join(" ")}: ${text}` : text);
}

function label(key) {
    const words = String(key).replace(/_/g, " ").trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
}
